<script setup lang="ts">
/**
 * A ribbon „big button“ that opens a menu instead of firing one action — to
 * fold a row of variants (chart types, symbols, sparklines) into one entry.
 * The trigger is a large NxRibbonButton (icon in the `icon` slot, label with a
 * caret); the menu is a kit menu panel in the top layer,
 * never clipped by the scrolling ribbon. Put `NxContextMenuItem`s (and
 * labels/separators) into the default slot; a click on one closes the menu.
 *
 * Enter, Space and ArrowDown open it with focus on the first entry; the rest
 * of the keyboard (arrows, Escape, Tab) is the menu's. `open-menu` fires on
 * every opening, so the host can load entries lazily.
 */
import { computed, ref, watch } from 'vue';
import NxMenuPanel from '../ContextMenu/NxMenuPanel.vue';
import NxRibbonButton from './NxRibbonButton.vue';

const props = withDefaults(
  defineProps<{
    label: string;
    title?: string;
    disabled?: boolean;
    /** Accessible name of the menu; defaults to the title or label. */
    menuLabel?: string;
    menuWidth?: 'sm' | 'md' | 'lg' | 'auto';
  }>(),
  { title: undefined, disabled: false, menuLabel: undefined, menuWidth: 'auto' }
);

const emit = defineEmits<{ 'open-menu': [] }>();

const open = ref(false);
const trigger = ref<InstanceType<typeof NxRibbonButton> | null>(null);
const triggerElement = computed(() => trigger.value?.element ?? null);

function toggle() {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) emit('open-menu');
}

// A group that becomes unavailable while its menu is open closes it.
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) open.value = false;
  }
);
</script>

<template>
  <NxRibbonButton
    ref="trigger"
    class="nx-ribbon-menu-button"
    :label="props.label"
    :title="props.title"
    :aria-label="props.title || props.label"
    :disabled="props.disabled"
    caret
    :aria-expanded="open"
    aria-haspopup="menu"
    data-testid="nx-ribbon-menu-button"
    @mousedown.prevent="toggle"
    @keydown.enter.prevent="toggle"
    @keydown.space.prevent="toggle"
    @keydown.down.prevent="!open && toggle()"
  >
    <template #icon><slot name="icon" /></template>
  </NxRibbonButton>
  <NxMenuPanel
    :open="open"
    :reference="triggerElement"
    placement="bottom-start"
    :offset="2"
    :width="props.menuWidth"
    :aria-label="props.menuLabel ?? props.title ?? props.label"
    :ignore="[triggerElement]"
    :return-focus-to="triggerElement"
    @close="open = false"
  >
    <slot />
  </NxMenuPanel>
</template>
